import { binaryToHex, EcdsaSecp256r1, } from '@streamr/utils';
const DEFAULT_WIDTH = 1200;
const DEFAULT_HEIGHT = 630;
const DEFAULT_QUALITY = 1.0;
const CONTENT_TYPE = 'image/jpeg';
async function createSignature(blob, privateKey) {
    const signingUtil = new EcdsaSecp256r1();
    const arrayBuffer = await blob.arrayBuffer();
    const data = new Uint8Array(arrayBuffer);
    const signature = await signingUtil.createSignature(data, privateKey);
    return binaryToHex(signature);
}
/**
 * Creates a thumbnail image from a video track
 * @param videoTrack The video track to capture the thumbnail from
 * @returns A promise that resolves to a Blob containing the thumbnail image
 */
export async function generateThumbnail(videoTrack) {
    // Create a video element to capture the frame
    const video = document.createElement('video');
    video.srcObject = new MediaStream([videoTrack]);
    video.muted = true;
    // Wait for video to be ready
    await new Promise((resolve) => {
        video.onloadedmetadata = () => resolve();
    });
    await video.play();
    // Create canvas and draw the video frame
    const canvas = document.createElement('canvas');
    canvas.width = DEFAULT_WIDTH;
    canvas.height = DEFAULT_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Could not get canvas context');
    }
    // Calculate dimensions to maintain aspect ratio
    const videoAspectRatio = video.videoWidth / video.videoHeight;
    const targetAspectRatio = DEFAULT_WIDTH / DEFAULT_HEIGHT;
    let drawWidth = DEFAULT_WIDTH;
    let drawHeight = DEFAULT_HEIGHT;
    let offsetX = 0;
    let offsetY = 0;
    if (videoAspectRatio > targetAspectRatio) {
        // Video is wider than target - fit to width
        drawHeight = DEFAULT_WIDTH / videoAspectRatio;
        offsetY = (DEFAULT_HEIGHT - drawHeight) / 2;
    }
    else {
        // Video is taller than target - fit to height
        drawWidth = DEFAULT_HEIGHT * videoAspectRatio;
        offsetX = (DEFAULT_WIDTH - drawWidth) / 2;
    }
    // Fill background (in case video doesn't fill canvas)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, DEFAULT_WIDTH, DEFAULT_HEIGHT);
    // Draw video maintaining aspect ratio
    ctx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
    // Clean up
    video.pause();
    video.srcObject = null;
    // Convert to blob
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, CONTENT_TYPE, DEFAULT_QUALITY));
    if (!blob) {
        throw new Error('Failed to create thumbnail blob');
    }
    console.log('Thumbnail generated', blob);
    return blob;
}
/**
 * Uploads a thumbnail blob to the backend server
 * @param blob The thumbnail blob to upload
 * @param streamId The stream ID to associate with the thumbnail
 * @param privateKey The private key to sign the thumbnail with
 * @returns A promise that resolves when the upload is complete
 * @throws Error if the upload fails or if THUMBNAIL_BACKEND_URL is not configured
 **/
export async function uploadThumbnail(imageBlob, streamId, privateKey, broadcasterUserId) {
    const baseUrl = import.meta.env.THUMBNAIL_BACKEND_URL;
    if (!baseUrl) {
        throw new Error('THUMBNAIL_BACKEND_URL environment variable is not set');
    }
    const signature = await createSignature(imageBlob, privateKey);
    const url = `${baseUrl}/thumbnail?streamId=${encodeURIComponent(streamId)}`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': CONTENT_TYPE,
            'X-Signature': signature,
            'X-Broadcaster-User-Id': broadcasterUserId,
        },
        body: imageBlob,
    });
    if (!response.ok) {
        throw new Error(`Failed to upload thumbnail: ${response.status} ${response.statusText}`);
    }
    console.log('Thumbnail uploaded');
}
